import type { VideoMeta } from '../types';

const DB_NAME = 'oemstream-videos';
const STORE = 'videos';
const DB_VERSION = 1;
const META_KEY = 'oemstream_videos_meta';
const VIDEO_ENDPOINT = '/api/localstate/videos';

interface StoredVideo extends VideoMeta {
  blob: Blob;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDB().then(db => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = run(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => db.close();
  }));
}

function readMeta(): VideoMeta[] {
  try {
    const raw = localStorage.getItem(META_KEY);
    return raw ? (JSON.parse(raw) as VideoMeta[]) : [];
  } catch {
    return [];
  }
}

function writeMeta(videos: VideoMeta[]): void {
  // META_KEY starts with 'oemstream_' so it is mirrored to the server-side
  // state file automatically by lib/persistence.
  localStorage.setItem(META_KEY, JSON.stringify(videos));
}

/**
 * Uploaded MP4 videos.
 *
 * Persistence strategy (survives origin/port changes):
 *  - Metadata lives in localStorage under an `oemstream_` key, which the
 *    persistence layer mirrors to the dev server's state file.
 *  - Blobs are PUT to the dev server (`.localstate/videos/<id>.mp4`).
 *  - IndexedDB is only a local read cache: on a fresh origin, blobs are
 *    fetched back from the server on demand.
 * In production (no middleware) uploads still work origin-locally via
 * IndexedDB; server calls fail silently.
 */
class VideoStore {
  async save(file: File): Promise<VideoMeta> {
    const meta: VideoMeta = {
      id: `vid_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      name: file.name,
      size: file.size,
      uploadedAt: new Date().toISOString(),
    };

    // Local read cache
    try {
      await tx('readwrite', store => store.put({ ...meta, blob: file } satisfies StoredVideo));
    } catch { /* IndexedDB unavailable — server copy still persists */ }

    // Server copy (canonical)
    try {
      await fetch(`${VIDEO_ENDPOINT}/${meta.id}`, { method: 'PUT', body: file });
    } catch { /* production — IndexedDB-only mode */ }

    writeMeta([meta, ...readMeta()]);
    return meta;
  }

  async list(): Promise<VideoMeta[]> {
    return readMeta().sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
  }

  async getBlob(id: string): Promise<Blob | null> {
    // 1) Local cache
    try {
      const record = await tx<StoredVideo | undefined>('readonly', store => store.get(id) as IDBRequest<StoredVideo | undefined>);
      if (record?.blob) return record.blob;
    } catch { /* fall through to server */ }

    // 2) Server copy (fresh origin after a port change)
    try {
      const res = await fetch(`${VIDEO_ENDPOINT}/${id}`);
      if (res.ok) {
        const blob = await res.blob();
        const meta = readMeta().find(v => v.id === id);
        if (meta) {
          try {
            await tx('readwrite', store => store.put({ ...meta, blob } satisfies StoredVideo));
          } catch { /* cache refill best-effort */ }
        }
        return blob;
      }
    } catch { /* production — no server copy */ }

    return null;
  }

  async remove(id: string): Promise<void> {
    try {
      await tx('readwrite', store => store.delete(id));
    } catch { /* ignore */ }
    try {
      await fetch(`${VIDEO_ENDPOINT}/${id}`, { method: 'DELETE' });
    } catch { /* ignore */ }
    writeMeta(readMeta().filter(v => v.id !== id));
  }
}

export const videoStore = new VideoStore();
export default VideoStore;
